<script setup lang="ts">
import { ProposedDelivery } from '@nuts/auto-delivery-sdk';
import { whenever } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { onMounted, ref, useTemplateRef, watch } from 'vue';

import AutoDeliveryLearnMore from '@/components/auto-delivery/AutoDeliveryLearnMore.vue';
import AutoDeliverySignUp from '@/components/auto-delivery/AutoDeliverySignUp.vue';
import AutoDeliveryFAQ from '@/components/auto-delivery/faq/AutoDeliveryFAQ.vue';
import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import Modal from '@/components/base/layout/Modal.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useCallback } from '@/composables/useCallback';
import { useAutoDelivery } from '@/stores/autoDelivery';

export type AutoDeliveryModalSteps = 'learn-more' | 'sign-up' | 'faq';

interface Props {
  isLoading?: boolean;
  isOpen: boolean;
  productDiscountNotice?: { header: string; message: string };
  showNextDeliveries?: boolean;
  showUpsellOption?: boolean;
}

const props = defineProps<Props>();

const interval = defineModel<number>('interval', { required: true });
const step = defineModel<AutoDeliveryModalSteps>('step', { required: true });

const emit = defineEmits<{
  'add-to-existing-delivery': [delivery?: ProposedDelivery, oneTime?: boolean];
  'handle-close': [];
  'start-auto-delivery': [];
}>();

const autoDeliveryStore = useAutoDelivery();
const { orders } = storeToRefs(autoDeliveryStore);

const headerRef = useTemplateRef('headerRef');
const initialStep = ref<AutoDeliveryModalSteps>('sign-up');

const handleClose = () => {
  if (step.value === initialStep.value) {
    emit('handle-close');
  } else {
    switch (step.value) {
      case 'learn-more':
        step.value = 'sign-up';
        break;
      case 'faq':
        step.value = 'learn-more';
        break;
      case 'sign-up':
      default:
        emit('handle-close');
        break;
    }
  }
};

const adjustFocus = async () => {
  const el = headerRef.value?.$el;
  if (el instanceof HTMLElement) {
    el.focus();
  }
};

const getNextDeliveries = useCallback(async () => {
  await autoDeliveryStore.getOrders({
    preloadPaymentMethod: false,
    preloadShippingAddresses: false,
  });
});

onMounted(() => {
  whenever(() => props.showNextDeliveries && props.isOpen, getNextDeliveries.execute, {
    immediate: true,
  });
  watch(
    () => props.isOpen,
    (isOpen) => {
      initialStep.value = isOpen ? step.value : 'sign-up';
    },
    { immediate: true },
  );
});
</script>

<template>
  <Modal
    anchor="right"
    aria-label="Auto-Delivery"
    data-test="auto-delivery-modal"
    :hasControls="false"
    :hasDefaultPadding="false"
    :isOpen
    skipFirstElementOnFocus
    width="md:max-w-lg"
    @handle-close="handleClose"
  >
    <template #body>
      <Header4 class="flex p-4 title" headerTag="span" ref="headerRef" tabindex="-1">
        <Transition :duration="300" name="fade" mode="out-in">
          <span v-if="step === 'sign-up'">Select an Auto-Delivery</span>
          <span v-else-if="step === 'learn-more'">
            Auto-Delivery <span class="sr-only">information</span>
          </span>
          <span v-else-if="step === 'faq'">Auto-Delivery FAQ</span>
        </Transition>
      </Header4>
      <transition
        class="flex flex-col items-start h-full"
        :data-test="`auto-delivery-modal-${step}-step`"
        :duration="300"
        mode="out-in"
        name="fade"
        @after-enter="adjustFocus"
      >
        <div v-if="step === 'sign-up'">
          <InformationBanner v-if="productDiscountNotice" class="p-4 mx-4 mt-2 mb-6" variant="info">
            <template #header>
              <BaseBodyText class="font-semibold">
                {{ productDiscountNotice.header }}
              </BaseBodyText>
            </template>
            <SmallBodyText class="" data-test="auto-delivery-discount-message">
              {{ productDiscountNotice.message }}
            </SmallBodyText>
          </InformationBanner>
          <AutoDeliverySignUp
            v-model="interval"
            class="px-4 mt-2 w-full"
            :nextDeliveries="showNextDeliveries ? orders as ProposedDelivery[] : []"
            :isLoading
            :showUpsellOption
            @add-to-existing-delivery="
              (delivery, oneTime) => $emit('add-to-existing-delivery', delivery, oneTime)
            "
            @learn-more="step = 'learn-more'"
            @start-auto-delivery="$emit('start-auto-delivery')"
          >
            <div v-if="getNextDeliveries.isPending" class="flex justify-center items-center mt-8">
              <img alt="Loading" class="w-4 h-4 invert animate-spin" src="@/assets/spinner.svg" />
            </div>
            <slot v-else />
          </AutoDeliverySignUp>
        </div>
        <div v-else-if="step === 'learn-more'">
          <AutoDeliveryLearnMore class="p-4 md:mt-3" showFAQButton @faq-click="step = 'faq'" />
        </div>
        <div v-else-if="step === 'faq'" class="flex flex-col px-4">
          <AutoDeliveryFAQ class="my-2 md:mt-4" />
        </div>
      </transition>
    </template>
  </Modal>
</template>

<style scoped>
.title {
  @apply md:leading-6 md:text-xl;
}
</style>
